// client/src/components/chat/MessageInput.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, TextField, Button, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import axiosInstance from '../../axiosInstance';

const SLASH_HELP = [
  '/me <action>       — action message',
  '/away [message]    — set away status',
  '/back              — return from away',
  '/busy              — set busy status',
  '/online            — set online status',
  '/join #room        — join a room',
  '/share             — share a collection item',
  '/help              — show commands',
].join('\n');

export default function MessageInput({ activeRoom, onSendMessage, onTypingStart, onTypingStop, onSetStatus, onJoinRoom }) {
  const [value, setValue] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [recentItems, setRecentItems] = useState([]);
  const [pickerCollection, setPickerCollection] = useState('games');
  const typingTimeout = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    return () => { clearTimeout(typingTimeout.current); };
  }, []);

  const loadShareItems = useCallback((collectionType) => {
    setPickerCollection(collectionType);
    const endpoint = {
      games: '/games', consoles: '/consoles', lego: '/lego', movies: '/movies',
      music: '/music', books: '/books', 'board-games': '/board-games',
    }[collectionType] || '/games';
    axiosInstance.get(`${endpoint}?limit=15`).then(res => {
      const items = (res.data.items || res.data || []).map(i => ({
        itemId: i._id,
        itemName: i.name || i.title,
        collectionType,
        itemType: collectionType === 'board-games' ? 'board-game' : collectionType === 'lego' ? 'lego' : collectionType.replace(/s$/, ''),
        itemImage: i.imageUrl || i.coverImage || null,
      }));
      setRecentItems(items);
    }).catch(() => setRecentItems([]));
  }, []);

  const handleTyping = (text) => {
    clearTimeout(typingTimeout.current);
    if (!text) {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        onTypingStop();
      }
      return;
    }
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      onTypingStart();
    }
    typingTimeout.current = setTimeout(() => {
      isTypingRef.current = false;
      onTypingStop();
    }, 2000);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setValue(text);
    handleTyping(text);
    setShowHelp(text === '/');
  };

  const handleShareItem = (item) => {
    onSendMessage({ type: 'item-share', sharedItem: item });
    setShowItemPicker(false);
  };

  const executeSlashCommand = useCallback((text) => {
    const parts = text.trim().split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1).join(' ');
    if (cmd === '/me') {
      if (args) onSendMessage({ content: args, type: 'action' });
    } else if (cmd === '/away') {
      onSetStatus('away', args || 'Away');
      onSendMessage({ content: `${args ? args + ' — ' : ''}is now away`, type: 'action' });
    } else if (cmd === '/back' || cmd === '/online') {
      onSetStatus('online', '');
      onSendMessage({ content: 'is back', type: 'action' });
    } else if (cmd === '/busy') {
      onSetStatus('busy', args || '');
      onSendMessage({ content: 'is now busy', type: 'action' });
    } else if (cmd === '/join') {
      const room = args.replace(/^#/, '');
      if (room && onJoinRoom) onJoinRoom(room);
    } else if (cmd === '/share') {
      loadShareItems('games');
      setShowItemPicker(true);
    } else if (cmd === '/help') {
      setShowHelp(true);
    } else {
      onSendMessage({ content: text, type: 'message' });
    }
  }, [onSendMessage, onSetStatus, onJoinRoom, loadShareItems]);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text) return;
    clearTimeout(typingTimeout.current);
    isTypingRef.current = false;
    onTypingStop();
    setValue('');
    setShowHelp(false);
    if (text.startsWith('/')) {
      executeSlashCommand(text);
    } else {
      onSendMessage({ content: text, type: 'message' });
    }
  }, [value, onSendMessage, onTypingStop, executeSlashCommand]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === 'Escape') { setShowHelp(false); setShowItemPicker(false); }
  };

  const overlayBoxSx = {
    position: 'absolute', bottom: '100%', left: 0, right: 0,
    bgcolor: 'background.paper',
    border: '1px solid rgba(255,255,255,0.12)',
    backdropFilter: 'blur(10px)',
    p: 1.5, mb: 0.5, borderRadius: 2, zIndex: 10,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {showHelp && (
        <Box sx={overlayBoxSx}>
          <Typography variant="caption" component="pre" sx={{ color: 'text.secondary', fontFamily: 'monospace', whiteSpace: 'pre', m: 0 }}>
            {SLASH_HELP}
          </Typography>
        </Box>
      )}

      {showItemPicker && (
        <Box sx={overlayBoxSx}>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, display: 'block', mb: 1 }}>
            Share a collection item
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {['games', 'consoles', 'lego', 'movies', 'music', 'books', 'board-games'].map(ct => (
              <Button key={ct} size="small" variant={pickerCollection === ct ? 'contained' : 'outlined'}
                onClick={() => loadShareItems(ct)}
                sx={{ fontSize: 11, px: 1, py: 0.25, minWidth: 0 }}>
                {ct}
              </Button>
            ))}
          </Box>
          <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
            {recentItems.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>No items found</Typography>
            )}
            {recentItems.map(item => (
              <Box key={item.itemId} onClick={() => handleShareItem(item)}
                sx={{ p: 0.75, cursor: 'pointer', borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(124,140,255,0.12)' } }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {item.itemName}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button size="small" onClick={() => setShowItemPicker(false)} sx={{ mt: 0.5, color: 'text.disabled' }}>
            Close
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1,
        bgcolor: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <TextField
          fullWidth size="small" value={value} onChange={handleChange} onKeyDown={handleKeyDown}
          placeholder={`Message #${activeRoom} — type / for commands`}
          inputProps={{ maxLength: 2000 }}
        />
        <Button variant="contained" onClick={handleSend} disabled={!value.trim()}
          sx={{ flexShrink: 0, minWidth: 0, px: 1.5 }} endIcon={<SendIcon />}>
          Send
        </Button>
      </Box>
    </Box>
  );
}
